import React, { Component } from "react";
import * as XLSX from "xlsx";
import { withApollo } from "react-apollo";
import gql from "graphql-tag";
import * as Cookies from "es-cookie";

// Student backend (exam engine) — results are recorded here, not on the college API.
const STUDENT_API = "https://rizee-student-backend-755214126997.us-south1.run.app/graphql";

// Exam picker — the institute's exams from "Manage Exams" (same list/source).
// paper.id is the exam_paper id used as schedule_exam_id for submission.
const GET_EXAM_PAPERS = gql`
  query($institution_id: Int!, $page: Int, $limit: Int) {
    manageQuestions(institution_id: $institution_id, exam_paper_id: 0, page: $page, limit: $limit) {
      totalCount
      papers {
        id
        exam_name
        name
        class
        start_time
        type
      }
    }
  }
`;

// Ordered question list for an exam paper (slno -> question_id, correct answer).
const GET_EXAM_QUESTIONS = `
  query($mobile: String!, $exam_session_id: Int!, $type: String, $exam_paper_id: Int) {
    getStudentExamQuestions(mobile: $mobile, exam_session_id: $exam_session_id, type: $type, exam_paper_id: $exam_paper_id) {
      id
      answer
      subject
    }
  }
`;

// Records a student's attempt — same mutation the student app fires on finish.
const FINISH_EXAM = `
  mutation($params: FinishStudentScheduleExam, $uid: String) {
    finishStudentScheduleExam(params: $params, uid: $uid)
  }
`;

// POST a GraphQL operation to the student backend.
async function studentGql(query, variables) {
  const res = await fetch(STUDENT_API, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ query, variables }),
  });
  const json = await res.json();
  if (json.errors && json.errors.length) {
    throw new Error(json.errors.map((e) => e.message).join("; "));
  }
  return json.data;
}

// 8-char random uid, mirroring the student app.
const makeUid = () => {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let s = "";
  for (let i = 0; i < 8; i++) s += chars.charAt(Math.floor(Math.random() * chars.length));
  return s;
};

// Normalize a marked option to A/B/C/D (accepts a/b/c/d or 1/2/3/4). Blank = not answered.
const normalizeAnswer = (v) => {
  const s = String(v == null ? "" : v).trim().toUpperCase();
  if (!s) return "";
  if (["A", "B", "C", "D"].includes(s)) return s;
  if (["1", "2", "3", "4"].includes(s)) return ["A", "B", "C", "D"][parseInt(s) - 1];
  return s;
};

class OmrUploadSection extends Component {
  state = {
    rows: null,
    fileName: "",
    error: "",
    submitting: false,
    progress: null, // { total, done, ok, fail }
    results: null,  // [{ mobile, ok, message }]
    // Exam picker (Manage Exams list)
    examSearch: "",
    allPapers: [],
    papersLoading: false,
    selectedPaper: null, // { id, name, className, type, startTime }
    pickerOpen: false,
  };

  componentDidMount() { this.loadPapers(); }

  loadPapers = async () => {
    const instId = parseInt(Cookies.get("institutionid")) || 118;
    this.setState({ papersLoading: true });
    try {
      const res = await this.props.client.query({
        query: GET_EXAM_PAPERS,
        variables: { institution_id: instId, page: 1, limit: 500 },
        fetchPolicy: "network-only",
      });
      const raw = (res.data && res.data.manageQuestions && res.data.manageQuestions.papers) || [];
      const allPapers = raw.map((p) => ({
        id: String(p.id),
        // p.name = exam_paper.exam_name (the actual paper name); p.exam_name is the
        // exam *type* (Exam-table lookup) — show the real paper name first.
        name: p.name || p.exam_name || `Exam ${p.id}`,
        className: p.class || "",
        type: p.type || "",
        startTime: p.start_time || "",
      }));
      this.setState({ allPapers, papersLoading: false });
    } catch (e) { console.error("load papers", e); this.setState({ allPapers: [], papersLoading: false }); }
  };

  onSearchChange = (v) => this.setState({ examSearch: v, pickerOpen: true });

  selectPaper = (p) => this.setState({ selectedPaper: p, pickerOpen: false, examSearch: "", results: null, progress: null, error: "" });

  handleUpload = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const wb = XLSX.read(ev.target.result, { type: "binary" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { defval: "" });
        this.setState({ rows, fileName: file.name, error: "", results: null, progress: null });
      } catch (err) {
        console.error("excel parse", err);
        this.setState({ error: "Could not read the Excel file.", rows: null });
      }
    };
    reader.readAsBinaryString(file);
    e.target.value = "";
  };

  clear = () => this.setState({ rows: null, fileName: "", error: "", results: null, progress: null });

  // Long-format sheet: one row per (student, question). Detect the columns
  // (case-insensitive): Mobile/roll, question_id, slno, attempt_answer, and the
  // optional status / question_time / reason_for_wrong / comments.
  resolveColumns = (rows) => {
    const cols = Object.keys(rows[0] || {});
    const find = (re) => cols.find((c) => re.test(c));
    return {
      mobileCol: find(/mobile|roll|phone|contact|login/i) || cols[0],
      qidCol: find(/question_?id|^qid$/i),
      slnoCol: find(/^sl\.?no$|^slno$|^s\.?no$|serial/i),
      answerCol: find(/attempt_?answer|^answer$|response|^ans$/i),
      statusCol: find(/^status$/i),
      timeCol: find(/question_?time|^time$/i),
      reasonCol: find(/reason/i),
      commentsCol: find(/comment/i),
    };
  };

  save = async () => {
    const { rows, selectedPaper } = this.state;
    if (!rows || !rows.length) return;
    const scheduleExamId = selectedPaper ? parseInt(selectedPaper.id) : 0;
    if (!scheduleExamId) {
      this.setState({ error: "Select an exam paper before uploading." });
      return;
    }

    const C = this.resolveColumns(rows);
    if (!C.qidCol) {
      this.setState({ error: "The Excel must have a question_id column." });
      return;
    }

    // Group rows by student (Mobile = login id). Unique students = one submission each.
    const byMobile = new Map();
    for (const row of rows) {
      const mobile = String(row[C.mobileCol] == null ? "" : row[C.mobileCol]).trim();
      if (!mobile) continue;
      if (!byMobile.has(mobile)) byMobile.set(mobile, []);
      byMobile.get(mobile).push(row);
    }
    const students = [...byMobile.entries()]; // [mobile, rows[]]
    const total = students.length;
    if (!total) {
      this.setState({ error: "No rows with a Mobile/roll value were found." });
      return;
    }
    this.setState({ submitting: true, error: "", results: null, progress: { total, done: 0, ok: 0, fail: 0 } });

    try {
      const num = (v, d) => { const n = parseInt(v); return isNaN(n) ? d : n; };
      const results = [];
      let ok = 0, fail = 0;
      for (let i = 0; i < students.length; i++) {
        const [mobile, qrows] = students[i];
        // Build this student's questions straight from the sheet rows.
        const questions = qrows.map((row, idx) => ({
          question_id: num(row[C.qidCol], 0),
          status: C.statusCol ? num(row[C.statusCol], 0) : 0,
          question_time: C.timeCol ? num(row[C.timeCol], 0) : 0,
          attempt_answer: C.answerCol ? normalizeAnswer(row[C.answerCol]) : "",
          reason_for_wrong: C.reasonCol ? num(row[C.reasonCol], 0) : 0,
          comments: C.commentsCol ? String(row[C.commentsCol] == null ? "" : row[C.commentsCol]) : "",
          slno: C.slnoCol ? num(row[C.slnoCol], idx + 1) : idx + 1,
        })).filter((q) => q.question_id);
        questions.sort((a, b) => a.slno - b.slno);

        if (!questions.length) {
          fail++; results.push({ mobile, ok: false, message: "No valid question rows" });
          this.setState({ progress: { total, done: i + 1, ok, fail } });
          continue;
        }
        const params = { mobile, source: 0, submitted_type: 1, schedule_exam_id: scheduleExamId, questions };
        try {
          await studentGql(FINISH_EXAM, { params, uid: makeUid() });
          ok++; results.push({ mobile, ok: true, message: `Submitted (${questions.length} Qs)` });
        } catch (err) {
          fail++; results.push({ mobile, ok: false, message: err.message || "Failed" });
        }
        this.setState({ progress: { total, done: i + 1, ok, fail } });
      }

      this.setState({ submitting: false, results });
    } catch (err) {
      console.error("upload results", err);
      this.setState({ submitting: false, error: err.message || "Upload failed." });
    }
  };

  render() {
    const { rows, fileName, error, submitting, progress, results, examSearch, allPapers, papersLoading, selectedPaper, pickerOpen } = this.state;
    const fmtDate = (st) => { let n = parseInt(st); if (!n) return ""; if (n < 1e12) n *= 1000; const d = new Date(n); return isNaN(d.getTime()) ? "" : d.toLocaleDateString(); };
    const q = (examSearch || "").trim().toLowerCase();
    const papers = q ? allPapers.filter((p) => String(p.name).toLowerCase().includes(q) || String(p.id).includes(q)) : allPapers;
    const cols = rows && rows.length ? Object.keys(rows[0]) : [];
    const preview = rows ? rows.slice(0, 25) : [];
    const S = {
      wrap: { background: "linear-gradient(135deg, #f0f4ff 0%, #faf5ff 100%)", padding: "20px 24px 60px", minHeight: "70vh" },
      card: { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 16, boxShadow: "0 1px 3px rgba(0,0,0,0.04)", padding: "18px 20px", marginBottom: 16 },
      header: { display: "flex", alignItems: "center", gap: 12 },
      iconChip: { width: 46, height: 46, borderRadius: 12, background: "#f5f3ff", border: "1px solid #ddd6fe", color: "#7c3aed", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 },
      h1: { fontSize: 22, fontWeight: 800, color: "#0f172a", margin: 0 },
      sub: { fontSize: 13, color: "#64748b", marginTop: 2 },
      btn: { background: "#16a34a", border: "none", borderRadius: 9, padding: "10px 18px", fontSize: 13, fontWeight: 700, color: "#fff", cursor: "pointer" },
      btnGhost: { background: "#fff", border: "1.5px solid #7c3aed", borderRadius: 9, padding: "10px 18px", fontSize: 13, fontWeight: 700, color: "#7c3aed", cursor: "pointer" },
      input: { border: "1.5px solid #ddd6fe", borderRadius: 9, padding: "9px 12px", fontSize: 13, fontWeight: 600, color: "#0f172a", width: 200 },
      th: { textAlign: "left", fontSize: 11, fontWeight: 700, color: "#7c3aed", textTransform: "uppercase", padding: "9px 10px", borderBottom: "2px solid #ede9fe", whiteSpace: "nowrap" },
      td: { fontSize: 13, color: "#334155", padding: "8px 10px", borderBottom: "1px solid #f3e8ff", whiteSpace: "nowrap" },
    };

    return (
      <div style={S.wrap}>
        <div style={S.card}>
          <div style={S.header}>
            <span style={S.iconChip}><i className="fal fa-cloud-upload" /></span>
            <div>
              <h1 style={S.h1}>Upload Results</h1>
              <div style={S.sub}>
                {fileName ? <span><i className="fal fa-file-excel" style={{ color: "#16a34a" }} /> {fileName} · {rows ? rows.length : 0} rows</span> : "Select an Excel (.xlsx/.xls) — one row per question per student. Columns: Mobile, question_id, slno, attempt_answer (optional: status, question_time, reason_for_wrong, comments). Rows are grouped by Mobile."}
              </div>
            </div>
          </div>
        </div>

        {/* Step 1 — pick the exam paper */}
        <div style={S.card}>
          <div style={{ fontSize: 13, fontWeight: 800, color: "#0f172a", marginBottom: 10 }}>
            <span style={{ background: "#7c3aed", color: "#fff", borderRadius: "50%", width: 20, height: 20, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11, marginRight: 8 }}>1</span>
            Select Exam Paper
          </div>
          {selectedPaper ? (
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <div style={{ background: "#f5f3ff", border: "1px solid #ddd6fe", borderRadius: 10, padding: "10px 14px", flex: 1, minWidth: 260 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>{selectedPaper.name}</div>
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                  ID {selectedPaper.id}{selectedPaper.className ? ` · ${selectedPaper.className}` : ""}{selectedPaper.type ? ` · ${selectedPaper.type}` : ""}{selectedPaper.startTime ? ` · ${fmtDate(selectedPaper.startTime)}` : ""}{selectedPaper.totalQuestions ? ` · ${selectedPaper.totalQuestions} Qs` : ""}
                </div>
              </div>
              <button style={S.btnGhost} onClick={() => this.setState({ selectedPaper: null })}><i className="fal fa-exchange" /> Change</button>
            </div>
          ) : (
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
                <input type="text" placeholder="Search exam by name or ID…" value={examSearch}
                  onChange={(e) => this.onSearchChange(e.target.value)}
                  style={{ ...S.input, width: 320, maxWidth: "100%" }} />
                <span style={{ fontSize: 12, color: "#64748b" }}>
                  {papersLoading ? <span><i className="fal fa-spinner fa-spin" /> Loading exams…</span> : `${papers.length} exam${papers.length === 1 ? "" : "s"}`}
                </span>
                <button style={{ ...S.btnGhost, padding: "6px 12px" }} onClick={this.loadPapers}><i className="fal fa-sync" /> Refresh</button>
              </div>
              <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, maxHeight: 340, overflowY: "auto" }}>
                {papersLoading ? (
                  <div style={{ padding: "18px", color: "#94a3b8", fontSize: 13 }}><i className="fal fa-spinner fa-spin" /> Loading exams…</div>
                ) : papers.length === 0 ? (
                  <div style={{ padding: "18px", color: "#94a3b8", fontSize: 13 }}>
                    No exams found for this institute. (Make sure you're on an institute that has exams in <b>Manage Exams</b>.)
                  </div>
                ) : papers.map((p) => (
                  <div key={p.id} onClick={() => this.selectPaper(p)}
                    style={{ padding: "10px 14px", borderBottom: "1px solid #f3e8ff", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "#faf5ff")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>{p.name}</div>
                      <div style={{ fontSize: 11, color: "#64748b" }}>ID {p.id}{p.className ? ` · ${p.className}` : ""}{p.type ? ` · ${p.type}` : ""}{p.startTime ? ` · ${fmtDate(p.startTime)}` : ""}</div>
                    </div>
                    <span style={{ ...S.btnGhost, padding: "5px 12px", fontSize: 12, whiteSpace: "nowrap" }}>Select</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Step 2 — upload the file */}
        <div style={S.card}>
          <div style={{ fontSize: 13, fontWeight: 800, color: "#0f172a", marginBottom: 10 }}>
            <span style={{ background: selectedPaper ? "#7c3aed" : "#cbd5e1", color: "#fff", borderRadius: "50%", width: 20, height: 20, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11, marginRight: 8 }}>2</span>
            Upload Results File
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", opacity: selectedPaper ? 1 : 0.5, pointerEvents: selectedPaper ? "auto" : "none" }}>
            <label style={{ ...S.btnGhost, margin: 0, display: "inline-flex", alignItems: "center", gap: 8 }}>
              <i className="fal fa-folder-open" /> Choose File
              <input type="file" accept=".xlsx,.xls" onChange={this.handleUpload} style={{ display: "none" }} />
            </label>
            {rows && rows.length > 0 && (
              <>
                <button style={{ ...S.btn, opacity: submitting ? 0.6 : 1 }} disabled={submitting} onClick={this.save}>
                  <i className="fal fa-cloud-upload" /> {submitting ? "Submitting…" : "Upload Results"}
                </button>
                <button style={S.btnGhost} onClick={this.clear}><i className="fal fa-times" /> Clear</button>
              </>
            )}
            {error && <span style={{ color: "#dc2626", fontSize: 13 }}>{error}</span>}
          </div>
          {!selectedPaper && <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 8 }}>Select an exam paper above first.</div>}
        </div>

        {progress && (
          <div style={S.card}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a", marginBottom: 8 }}>
              Submitting {progress.done}/{progress.total} · <span style={{ color: "#16a34a" }}>{progress.ok} ok</span> · <span style={{ color: "#dc2626" }}>{progress.fail} failed</span>
            </div>
            <div style={{ height: 8, background: "#ede9fe", borderRadius: 6, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${Math.round((progress.done / progress.total) * 100)}%`, background: "#7c3aed", transition: "width .2s" }} />
            </div>
          </div>
        )}

        {results && (
          <div style={S.card}>
            <div style={{ fontSize: 14, fontWeight: 800, color: "#0f172a", marginBottom: 10 }}>
              Result — {results.filter((x) => x.ok).length} submitted, {results.filter((x) => !x.ok).length} failed
            </div>
            <div style={{ overflowX: "auto", maxHeight: 320, overflowY: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr><th style={S.th}>#</th><th style={S.th}>Roll / Mobile</th><th style={S.th}>Status</th><th style={S.th}>Message</th></tr></thead>
                <tbody>
                  {results.map((r, i) => (
                    <tr key={i} style={{ background: r.ok ? "#fff" : "#fef2f2" }}>
                      <td style={S.td}>{i + 1}</td>
                      <td style={S.td}>{r.mobile}</td>
                      <td style={{ ...S.td, fontWeight: 700, color: r.ok ? "#16a34a" : "#dc2626" }}>{r.ok ? "✓ OK" : "✕ Failed"}</td>
                      <td style={S.td}>{r.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {rows && rows.length > 0 && !results && (
          <div style={S.card}>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr>{cols.map((c) => <th key={c} style={S.th}>{c}</th>)}</tr></thead>
                <tbody>
                  {preview.map((r, i) => (
                    <tr key={i} style={{ background: i % 2 ? "#faf5ff" : "#fff" }}>
                      {cols.map((c) => <td key={c} style={S.td}>{r[c] !== undefined && r[c] !== null && r[c] !== "" ? r[c] : "-"}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {rows.length > preview.length && (
              <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 10 }}>Showing first {preview.length} of {rows.length} rows.</div>
            )}
          </div>
        )}
      </div>
    );
  }
}

export default withApollo(OmrUploadSection);
